import { BaseElement } from 'base';

class DateField extends BaseElement {
  #handlers = new Set();

  onConnect() {
    this.trigger = this.q('.select__trigger');
    this.picker = this.q('date-picker');
    this.hiddenInput = this.q('input[type="hidden"]');
    this.valueEl = this.q('[data-date-field-value]');
    this.message = this.q('.select__message');
    this.messageText = this.message?.lastElementChild;
    this.hintText = this.dataset.hint ?? '';
    this.hintHidden = this.hintText === '';

    if (!this.trigger || !this.picker) {
      return;
    }

    this.lang = document.documentElement.lang || 'en';
    this.dateFormatter = new Intl.DateTimeFormat(this.lang, {
      dateStyle: this.dataset.dateStyle || 'medium',
      timeZone: 'UTC'
    });

    if (this.hiddenInput?.value) this.formatValue(this.hiddenInput.value);

    this.listen(this.trigger, 'click', this.onTriggerClick);
    this.listen(this.trigger, 'keydown', this.onTriggerKeydown);
    this.listen(this, 'keydown', this.onHostKeydown);
    this.cleanup(this.picker.onChange(state => this.onPick(state)));
    this.listen(document, 'pointerdown', this.onDocumentPointerdown);
    this.listen(document, 'focusin', this.onDocumentFocusin);
    this.listen(this, 'focusout', this.onFocusOut);
  }

  get isOpen() {
    return this.trigger.getAttribute('aria-expanded') === 'true';
  }

  open() {
    if (this.trigger.disabled || this.isOpen) return;
    this.picker.open();
    this.trigger.setAttribute('aria-expanded', 'true');
    this.picker.focusRoving();
  }

  close({ restoreFocus = false } = {}) {
    if (!this.isOpen) return;
    this.picker.close();
    this.trigger.setAttribute('aria-expanded', 'false');
    if (restoreFocus) this.trigger.focus();
  }

  onTriggerClick() {
    if (this.isOpen) this.close({ restoreFocus: true });
    else this.open();
  }

  onTriggerKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.open();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.onTriggerClick();
        break;
    }
  }

  onHostKeydown(event) {
    if (event.key !== 'Escape' || !this.isOpen) return;
    event.preventDefault();
    this.close({ restoreFocus: true });
  }

  onDocumentPointerdown(event) {
    if (this.isOpen && !this.contains(event.target)) this.close();
  }

  onDocumentFocusin(event) {
    if (this.isOpen && !this.contains(event.target)) this.close();
  }

  onFocusOut(event) {
    if (!this.isOpen || !event.relatedTarget) return;
    if (!this.contains(event.relatedTarget)) this.close();
  }

  formatValue(iso) {
    const [year, month, day] = iso.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    if (this.valueEl)
      this.valueEl.textContent = this.dateFormatter.format(date);
    this.setAttribute('data-filled', 'true');
  }

  onPick({ value }) {
    if (this.hiddenInput) {
      this.hiddenInput.value = value;
      this.hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.formatValue(value);
    this.close({ restoreFocus: true });
    this.#emit({ id: this.id, value });
  }

  setError(message) {
    this.setAttribute('data-state', 'error');
    if (this.messageText) this.messageText.textContent = message;
    if (this.message) this.message.hidden = false;
    this.trigger?.setAttribute('aria-invalid', 'true');
  }

  clearError() {
    this.removeAttribute('data-state');
    if (this.messageText) this.messageText.textContent = this.hintText;
    if (this.message) this.message.hidden = this.hintHidden;
    this.trigger?.removeAttribute('aria-invalid');
  }

  onChange(handler) {
    this.#handlers.add(handler);
    return () => this.#handlers.delete(handler);
  }

  onDisconnect() {
    this.#handlers.clear();
  }

  #emit(detail) {
    for (const handler of [...this.#handlers]) {
      try {
        handler(detail);
      } catch (error) {
        console.error('[date-field] change handler failed:', error);
      }
    }
  }
}

if (!customElements.get('date-field'))
  customElements.define('date-field', DateField);
